/**
 * The home screen — Blockari's `Home` in the kit's markup.
 *
 * The hero (the wordmark and the tagline), three mode cards, then the strip:
 * How it works beside the account box with the XP bar and the recent
 * challenges. One page, scrolled, under the sticky bar whose wordmark hides
 * while the hero's is on screen.
 */

import { TopThisWeek } from '@/ui/leaderboard';
import { ROUND_SECONDS } from '@/game/types';
import { rankFor, tierNumeral, xpThreshold } from '@/game/xp';
import type { ChallengeCard, RankView } from '@/net/api';
import { faucetEntry, type FaucetEntry } from '@/ui/faucet-entry';
import { el, formatScore } from '@/ui/hud';
import {
  TopBar,
  avatarRing,
  guestNote,
  hueOf,
  initialsOf,
  kitButton,
  kitXpChipSm,
  patchRows,
  rankBadgeCard,
  rankClass,
  timeline,
  type StartScreenCallbacks,
} from '@/ui/screens';

/** A hand of three cards, fanned — the mode glyph, in the mode's colour. */
function fanGlyph(): string {
  const card = (x: number, rot: number, lit: boolean): string =>
    `<g transform="translate(${x} 50) rotate(${rot})"><rect x="-17" y="-24" width="34" height="48" rx="4" fill="${lit ? 'var(--mode, var(--accent))' : 'rgb(var(--text-rgb) / 0.14)'}" stroke="rgb(var(--white-rgb) / 0.35)" stroke-width="1.2"/>${lit ? '<text x="-11" y="-9" font-family="var(--font-display)" font-weight="700" font-size="11" fill="var(--c-bg-deep)">21</text>' : ''}</g>`;
  return `<svg viewBox="0 0 100 100" width="72" height="72" aria-hidden="true"><g style="filter: drop-shadow(0 0 6px rgb(var(--mode-rgb, var(--accent-rgb)) / 0.6))">${card(34, -14, false)}${card(50, 0, false)}${card(66, 14, true)}</g></svg>`;
}

/**
 * The how-it-works illustrations, 56 x 56 (Blockari draws a little board for each step; 21 Wild draws cards):
 * the same deck dealt to both, a pile that makes 21, and the pot.
 */
function stepGlyph(kind: 'deck' | 'twentyone' | 'pot'): string {
  const card = (x: number, y: number, rot: number, face: string, fill: string, ink: string): string =>
    `<g transform="translate(${x} ${y}) rotate(${rot})"><rect x="-15" y="-21" width="30" height="42" rx="4" fill="${fill}" stroke="rgb(var(--white-rgb) / 0.35)" stroke-width="1.2"/><text x="0" y="6" text-anchor="middle" font-family="var(--font-display)" font-weight="700" font-size="16" fill="${ink}">${face}</text></g>`;
  const dim = 'rgb(var(--text-rgb) / 0.16)';
  const lit = 'var(--accent)';
  const warm = 'var(--accent-warm)';
  const ink = 'var(--c-bg-deep)';
  let body = '';
  if (kind === 'deck') body = card(36, 50, -10, '', dim, ink) + card(48, 50, 0, '', dim, ink) + card(62, 50, 10, '', lit, ink);
  else if (kind === 'twentyone') body = card(34, 54, -12, '10', dim, 'var(--text)') + card(52, 50, 0, '10', dim, 'var(--text)') + card(68, 54, 12, 'A', lit, ink);
  else body = `<circle cx="50" cy="50" r="30" fill="${warm}" opacity="0.95"/><circle cx="50" cy="50" r="22" fill="rgb(var(--ink-rgb) / 0.7)"/>` + card(50, 50, 0, '21', lit, ink).replace('x="-15" y="-21" width="30" height="42"', 'x="-10" y="-14" width="20" height="28"');
  return `<svg viewBox="0 0 100 100" width="56" height="56" aria-hidden="true"><g style="filter: drop-shadow(0 0 5px rgb(var(--accent-rgb) / 0.4))">${body}</g></svg>`;
}

const ICON_ARROW =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>';

export class StartScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;
  readonly playButton: HTMLAnchorElement;
  readonly challengeButton: HTMLAnchorElement;
  readonly lobbyButton: HTMLAnchorElement;
  readonly tutorialButton: HTMLButtonElement;
  readonly signOutButton: HTMLButtonElement;
  readonly historyButton: HTMLAnchorElement;

  readonly #status = el('p', 'w21-hero-status');
  readonly #modes = el('div', 'w21-modes');
  readonly #strip = el('div', 'w21-strip');
  readonly #account = el('div', 'w21-panel');
  /** The guest's "this is only kept in this browser" line, between who they are and their XP bar. */
  readonly #guestNote = el('div', 'w21-guest-note-slot');
  readonly #top = new TopThisWeek(() => this.callbacks.onLeaderboard?.());
  readonly #who = el('div', 'w21-who');
  readonly #whoXp = el('div', 'w21-who-xp');
  readonly #recent = el('div', 'w21-recent-list');
  readonly #recentDivider = el('div', 'w21-divider w21-labelled', 'Recent');
  #observer: IntersectionObserver | null = null;
  /** The account box's "Free tokens", when the faucet is on (the host passes `onFaucet` only then). */
  readonly #faucet: FaucetEntry | null;

  constructor(private readonly callbacks: StartScreenCallbacks) {
    this.#faucet = callbacks.onFaucet ? faucetEntry(() => callbacks.onFaucet?.()) : null;
    // The screen scrolls; the page inside is the kit's `main.page`.
    this.root = el('div', 'w21-screen w21-screen--start');
    this.root.dataset.active = 'false';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', '21 Wild — start');

    this.topBar = new TopBar(
      callbacks.bar ?? {
        onHome: () => undefined,
        onHistory: () => callbacks.onHistory(),
        onProfile: () => callbacks.onProfile(),
      },
    );
    this.root.append(this.topBar.root);

    const page = el('main', 'w21-page');
    const route = el('div', 'w21-route');

    /* -- the hero ------------------------------------------------------------ */
    const hero = el('section', 'w21-hero');
    const logo = el('div', 'w21-hero-logo');
    const img = el('img') as HTMLImageElement;
    img.src = `${import.meta.env.BASE_URL}logo.webp`;
    img.srcset = `${import.meta.env.BASE_URL}logo@1x.webp 700w, ${import.meta.env.BASE_URL}logo.webp 1400w`;
    img.sizes = '560px';
    img.width = 560;
    img.height = 335;
    img.alt = '21 Wild';
    img.decoding = 'async';
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');
    img.draggable = false;
    logo.append(img);
    const tagline = el('p', 'w21-hero-tagline');
    const phrase = (a: string, b: string, em = false): HTMLElement => {
      const span = el('span', 'w21-phrase');
      span.append(document.createTextNode(a), el('span', 'w21-dot'), em ? el('em', undefined, b) : document.createTextNode(b));
      return span;
    };
    tagline.append(phrase('Same deck', 'Same cards'), phrase('Three minutes', 'Best score wins', true));
    // No "today's best" in the hero: Blockari's hero is the wordmark and the
    // tagline, and the page's blocks are the kit's. The status line is kept
    // for the lobby's word on a shared link, out of the flow until it speaks.
    this.#status.hidden = true;
    hero.append(logo, tagline, this.#status);

    /* -- the three modes ------------------------------------------------------ */
    const mode = (
      cls: string,
      tag: string,
      title: string,
      blurb: string,
      cta: string,
      k: number,
      on: () => void,
    ): HTMLAnchorElement => {
      const card = el('a', cls) as HTMLAnchorElement;
      card.href = '#';
      card.style.setProperty('--k', String(k));
      const glyph = el('span', 'w21-mode-glyph');
      glyph.innerHTML = fanGlyph();
      card.append(
        glyph,
        el('span', 'w21-tag', tag),
        el('h3', undefined, title),
        el('p', undefined, blurb),
        el('span', 'w21-cta', cta),
      );
      card.addEventListener('click', (event) => {
        event.preventDefault();
        on();
      });
      return card;
    };
    this.playButton = mode(
      'w21-mode-card',
      'Solo',
      'Practice',
      'A random deck, no fee, no opponent. Pause any time.',
      'Play now',
      0,
      () => this.callbacks.onStart(),
    );
    this.lobbyButton = mode(
      'w21-mode-card w21-amber',
      'Create',
      'Create Challenge',
      'Stake $CHAIN, play your deck, and wait for a challenger to beat your score.',
      'Set the bar',
      1,
      () => this.callbacks.onLobby(),
    );
    this.challengeButton = mode(
      'w21-mode-card w21-rose',
      'Take',
      'Take Challenge',
      'Match an open challenge. You get their exact cards. Beat them.',
      'Find a match',
      2,
      () => this.callbacks.onAsync(),
    );
    this.#modes.append(this.playButton, this.lobbyButton, this.challengeButton);

    /* -- the strip: how it works, and the account --------------------------- */
    const how = el('div', 'w21-panel');
    const howHead = el('div', 'w21-panel-head');
    const minutes = Math.floor(ROUND_SECONDS / 60);
    howHead.append(
      el('h3', undefined, 'How it works'),
      el('span', 'w21-chip w21-indigo', `${minutes}:${String(ROUND_SECONDS % 60).padStart(2, '0')} on the clock`),
    );
    const steps = el('div', 'w21-steps');
    for (const [n, title, body, kind] of [
      ['01', 'Same deck', 'Both players get the identical cards in the same order. No luck, only play.', 'deck'],
      ['02', 'Make 21', 'Land a pile on 21 or five cards under it to clear it. Chain clears for a streak.', 'twentyone'],
      ['03', 'Winner takes the pot', 'Higher score takes both wagers. Ties go to the faster finish.', 'pot'],
    ] as const) {
      const step = el('div', 'w21-step');
      step.innerHTML = stepGlyph(kind);
      const text = el('div');
      text.append(el('span', 'w21-step-n', n), el('b', undefined, title), el('span', undefined, body));
      step.append(text);
      steps.append(step);
    }
    // The walkthrough, asked for outright. Adam's call: in the How-it-works
    // panel, as a button — where somebody looking for how to play looks —
    // rather than a link in the small print, where it went unfound. Plain,
    // not primary: the page's primaries are the three ways to deal, and the
    // tutorial competing with them is why it was in the small print at all.
    this.tutorialButton = el('button', 'w21-btn w21-block w21-tutorial', 'Tutorial') as HTMLButtonElement;
    this.tutorialButton.type = 'button';
    this.tutorialButton.addEventListener('click', () => this.callbacks.onTutorial());
    // Blockari's panel has the Tutorial button alone; the full guide is reached from the inbox foot, the
    // profile's "How it works" link and the 2\u00d7 chip.
    how.append(howHead, steps, this.tutorialButton);

    this.#recentDivider.style.margin = '16px 0 4px';
    this.#account.append(this.#who, this.#guestNote, this.#whoXp, this.#recentDivider, this.#recent);
    this.#account.hidden = true;
    this.#account.classList.add('w21-strip-who');
    // Blockari's "Top this week": the weekly XP podium and my place, under How it works.
    this.#strip.append(how, this.#account, this.#top.root);

    // The bar's targets, kept as members for callers that press them.
    this.signOutButton = el('button', 'w21-btn w21-sm w21-ghost', 'Sign out') as HTMLButtonElement;
    this.signOutButton.type = 'button';
    this.signOutButton.addEventListener('click', () => this.callbacks.onSignOut());
    this.historyButton = kitButton('All challenges', 'w21-btn w21-ghost w21-sm', () => this.callbacks.onHistory());
    this.historyButton.innerHTML += ICON_ARROW;
    this.historyButton.style.justifySelf = 'start';

    const legal = el(
      'p',
      'w21-legal',
      'Alpha build. $CHAIN balances here are off-chain play tokens with no value. On-chain wallets come later.',
    );

    route.append(hero, this.#modes, this.#strip, legal);
    page.append(route);
    this.root.append(page);

    // `.scrolled` past 40 px, and `.brand-hidden` while the hero wordmark is
    // in view: the 28 px mark holds the brand slot and the bar's wordmark
    // hides, so the name is never on screen twice. The observer's root is the
    // screen, since the screen is the scroller.
    this.root.addEventListener('scroll', () => this.topBar.setScrolled(this.root.scrollTop > 40), {
      passive: true,
    });
    this.topBar.setBrandHidden(true);
    if (typeof IntersectionObserver !== 'undefined') {
      this.#observer = new IntersectionObserver(
        ([entry]) => this.topBar.setBrandHidden(entry?.isIntersecting ?? true),
        { root: this.root, threshold: 0.05 },
      );
      this.#observer.observe(logo);
    }
  }

  /**
   * The score to beat. Not shown: the home page is the kit's, and a daily
   * best has no block in it — if it comes back it belongs in the account
   * panel as a stat line, not in the hero. Kept so the caller's wiring holds.
   */
  setTarget(
    _scores: {
      today: { name: string; score: number } | null;
      allTime: { name: string; score: number } | null;
    } | null,
  ): void {
    // Deliberately nothing.
  }

  /**
   * Show who is signed in and what they hold, or hide the box entirely.
   *
   * Hidden rather than showing a placeholder while the session is still being
   * resolved: a balance that flicks from nothing to a number is worse than one
   * that simply arrives.
   */
  setAccount(
    account:
      | {
          username: string;
          balance: number;
          isGuest?: boolean;
          rank?: RankView;
          xp?: number;
          xpLevel?: number;
          prevThreshold?: number;
          nextThreshold?: number;
          xpProgress?: number;
        }
      | null,
  ): void {
    this.topBar.setAccount(account);
    this.#top.refresh(account?.username ?? null);
    this.#faucet?.setAccount(account?.username ?? null, account?.isGuest === true);

    if (!account || !account.rank || account.xpLevel === undefined) {
      this.#account.hidden = true;
      return;
    }
    const rank = account.rank;

    /* -- who ----------------------------------------------------------------- */
    this.#who.className = `w21-who ${rankClass(rank.index)}`;
    const avatar = el('span', `w21-avatar w21-lg w21-ringed ${rankClass(rank.index)}`, initialsOf(account.username));
    avatar.setAttribute('aria-hidden', 'true');
    avatar.style.setProperty('--hue', String(hueOf(account.username)));
    avatar.append(avatarRing(account.xpProgress ?? 0));

    const body = el('div');
    body.style.minWidth = '0';
    const name = el('div', 'w21-name');
    name.append(kitButton(account.username, '', () => this.callbacks.onProfile()));
    const line = el('div', 'w21-rank-line');
    line.append(
      rankBadgeCard(rank, 24),
      el('span', 'w21-rank-name', `${rank.name} ${tierNumeral(rank.tier)}`),
      kitXpChipSm(account.xpLevel, rank),
    );
    body.append(
      name,
      line,
      el(
        'div',
        'w21-sub',
        `${account.isGuest ? 'Guest session' : 'Signed in'} · ${formatScore(account.balance)} $CHAIN`,
      ),
    );

    const actions = el('div', 'w21-actions');
    actions.append(kitButton('Profile', 'w21-btn w21-sm', () => this.callbacks.onProfile()));
    // Blockari: Profile · Free tokens · Sign out. Absent while the faucet is off.
    if (this.#faucet) actions.append(this.#faucet.root);
    actions.append(this.signOutButton);
    this.#who.replaceChildren(avatar, body, actions);

    /* -- the XP bar --------------------------------------------------------- */
    const xp = account.xp ?? xpThreshold(account.xpLevel);
    const prev = account.prevThreshold ?? xpThreshold(account.xpLevel);
    const next = account.nextThreshold ?? xpThreshold(account.xpLevel + 1);
    const p = Math.min(1, Math.max(0, (xp - prev) / Math.max(1, next - prev)));
    const bar = el('div', `w21-xp-bar w21-md ${rankClass(rank.index)} w21-reveal`);
    bar.style.setProperty('--p', String(p));
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', String(prev));
    bar.setAttribute('aria-valuemax', String(next));
    bar.setAttribute('aria-valuenow', String(Math.round(xp)));
    bar.setAttribute('aria-label', `XP level ${account.xpLevel}: ${formatScore(Math.round(xp))} of ${formatScore(next)}`);
    const track = el('div', 'w21-xp-track');
    track.append(el('i', 'w21-xp-fill'));
    const legend = el('div', 'w21-xp-legend');
    const nums = el('span', 'w21-xp-nums');
    nums.append(el('b', undefined, formatScore(Math.round(xp))), document.createTextNode(` / ${formatScore(next)} XP`));
    const after = rankFor(account.xpLevel + 1);
    legend.append(
      nums,
      el('span', 'w21-xp-next', `${formatScore(Math.max(0, next - Math.round(xp)))} to ${after.name} ${tierNumeral(after.tier)}`),
    );
    bar.append(track, legend);
    this.#whoXp.replaceChildren(bar);

    // A guest is told, here where they see their balance, that it lives in this browser only.
    if (account.isGuest && this.callbacks.onCreateAccount) {
      const onCreate = this.callbacks.onCreateAccount.bind(this.callbacks);
      this.#guestNote.replaceChildren(guestNote(onCreate));
    } else {
      this.#guestNote.replaceChildren();
    }
    this.#account.hidden = false;
  }

  /**
   * The player's last few challenges, under a "Recent" divider: the first
   * three, as the compact card — head, status, when — with "All challenges"
   * after them.
   */
  setRecent(rows: ChallengeCard[]): void {
    // The divider and the list stay at zero, with the empty line where the
    // cards would be — a fresh guest sees where their record will go.
    if (rows.length === 0) {
      this.#recent.replaceChildren(
        el('p', 'w21-muted', 'No challenges yet. Take one and it shows up here.'),
        this.historyButton,
      );
      return;
    }
    // A card opens ITS challenge, as on the profile and history lists.
    const open = (id: string): void => (this.callbacks.onOpenChallenge ?? this.callbacks.onHistory)(id);
    const list = timeline(rows.slice(0, 3), open);
    this.#recent.replaceChildren(...Array.from(list.children), this.historyButton);
  }

  /**
   * The recent cards again from a newer fetch, while home is up: the cards that are the same are left alone, one that
   * changed (a settled challenge) is swapped without the entrance animation, and nothing blanks. First rows after an
   * empty card, or none after some, are the plain `setRecent`: there is nothing to patch.
   */
  refreshRecent(rows: ChallengeCard[]): void {
    if (rows.length === 0 || !this.#recent.querySelector('.w21-match')) {
      this.setRecent(rows);
      return;
    }
    const open = (id: string): void => (this.callbacks.onOpenChallenge ?? this.callbacks.onHistory)(id);
    // The "All challenges" button is not a card: it is set aside while the cards are patched, then put back last.
    this.historyButton.remove();
    patchRows(this.#recent, timeline(rows.slice(0, 3), open));
    this.#recent.append(this.historyButton);
  }

  showDailyGrant(amount: number): void {
    this.topBar.showDailyGrant(amount);
  }

  setStatus(message: string, tone: 'neutral' | 'rose' = 'neutral'): void {
    this.#status.textContent = message;
    this.#status.dataset.tone = tone;
    this.#status.hidden = message.length === 0;
  }

  /**
   * Present the screen as an invitation rather than a menu: someone who
   * followed a shared link has already made their choice, so the solo card
   * becomes the one that accepts it and the other two stand down.
   */
  setInvite(inviting: boolean): void {
    const title = this.playButton.querySelector('h3');
    if (title) title.textContent = inviting ? 'Accept challenge' : 'Practice';
    this.challengeButton.hidden = inviting;
    this.lobbyButton.hidden = inviting;
    this.root.dataset.invite = inviting ? 'true' : 'false';
  }

  /** Stop a multiplayer card being pressed twice while one call is in flight. */
  setBusy(busy: boolean): void {
    this.challengeButton.setAttribute('aria-disabled', String(busy));
    this.lobbyButton.setAttribute('aria-disabled', String(busy));
  }

  show(): void {
    this.root.dataset.active = 'true';
    // One entrance: the kit's own (`logo-in`, `route-in`, the cards' stagger).
    // The gsap reveal that used to run on top of it doubled every move.
  }

  hide(): void {
    this.root.dataset.active = 'false';
  }

  destroy(): void {
    this.#observer?.disconnect();
    this.#faucet?.destroy();
    this.root.remove();
  }
}
